/**
 * Phonetic Converter Utility
 * Provides functions to convert phonetics for phrases and vocabulary
 */

import {
  convertToBritishPhonetic,
  convertToBothPhonetics,
} from '../data/phonetics';

/**
 * Convert phrase data to include both American and British phonetics
 * @param phraseData - Phrase with only generic phonetic
 * @returns Phrase with both American and British variants
 */
export function convertPhrasePhonetics<T extends { phonetic?: string; phoneticUS?: string; phoneticUK?: string }>(
  phraseData: T
): T & { phoneticUS: string; phoneticUK: string } {
  const { phonetic, phoneticUS, phoneticUK } = phraseData;

  // If specific variants already exist, use them
  if (phoneticUS && phoneticUK) {
    return { ...phraseData, phoneticUS, phoneticUK };
  }

  // If only generic phonetic exists, convert it
  if (phonetic) {
    const converted = convertToBothPhonetics(phonetic);
    return {
      ...phraseData,
      phoneticUS: phoneticUS || converted.american,
      phoneticUK: phoneticUK || converted.british,
    };
  }

  // No phonetic available, return empty strings
  return {
    ...phraseData,
    phoneticUS: '',
    phoneticUK: '',
  };
}

/**
 * Batch convert array of phrase data
 * @param phrases - Array of phrases with generic phonetics
 * @returns Array of phrases with both variants
 */
export function batchConvertPhonetics<T extends { phonetic?: string; phoneticUS?: string; phoneticUK?: string }>(
  phrases: T[]
): (T & { phoneticUS: string; phoneticUK: string })[] {
  return phrases.map(convertPhrasePhonetics);
}

/**
 * Get phonetic display based on user preference
 * @param phrase - Phrase object
 * @param style - Preferred pronunciation style
 * @returns Phonetic string to display
 */
export function getDisplayPhonetic<T extends { phonetic?: string; phoneticUS?: string; phoneticUK?: string }>(
  phrase: T,
  style: 'american' | 'british' = 'american'
): string {
  // Convert if needed
  const converted = convertPhrasePhonetics(phrase);

  // Return based on preference
  return style === 'american' ? converted.phoneticUS : converted.phoneticUK;
}

/**
 * Generate comparison text showing both phonetics
 * @param phrase - Phrase object
 * @returns Formatted comparison string
 */
export function getPhoneticComparison<T extends { phonetic?: string; phoneticUS?: string; phoneticUK?: string }>(
  phrase: T
): { american: string; british: string; isDifferent: boolean } {
  const converted = convertPhrasePhonetics(phrase);

  return {
    american: converted.phoneticUS,
    british: converted.phoneticUK,
    isDifferent: converted.phoneticUS !== converted.phoneticUK,
  };
}

/**
 * Highlight phonetic differences between American and British
 * @param phrase - Phrase object
 * @returns Text with highlighted differences
 */
export function highlightPhoneticDifferences<T extends { phonetic?: string; phoneticUS?: string; phoneticUK?: string }>(
  phrase: T
): {
  american: string;
  british: string;
  differences: Array<{ american: string; british: string; position: number }>;
} {
  const converted = convertPhrasePhonetics(phrase);

  // Find differences by comparing character by character
  const differences: Array<{ american: string; british: string; position: number }> = [];
  const american = converted.phoneticUS;
  const british = converted.phoneticUK;

  // Simple comparison - could be improved with more sophisticated matching
  const maxLen = Math.max(american.length, british.length);
  for (let i = 0; i < maxLen; i++) {
    if (american[i] !== british[i]) {
      // Find the IPA symbol around this position
      let symbolStart = i;
      let symbolEnd = i + 1;

      // Look for IPA symbol boundaries (usually marked by /)
      while (symbolStart > 0 && american[symbolStart - 1] !== '/') symbolStart--;
      while (symbolEnd < american.length && american[symbolEnd] !== '/') symbolEnd++;

      const americanSymbol = american.slice(symbolStart, symbolEnd);
      const britishSymbol = british.slice(symbolStart, Math.min(symbolEnd, british.length));

      if (americanSymbol !== britishSymbol) {
        differences.push({
          american: americanSymbol,
          british: britishSymbol,
          position: symbolStart,
        });
      }
      break; // Only show first major difference for simplicity
    }
  }

  return {
    american,
    british,
    differences,
  };
}

/**
 * Convert generic phonetic string to both styles (simple version)
 * This is a convenience function for one-off conversions
 */
export function convertPhoneticString(phonetic: string): { american: string; british: string } {
  return convertToBothPhonetics(phonetic);
}

/**
 * Test conversions for common phrases
 * Example usage and validation
 */
export function testPhoneticConversions(): void {
  const testCases = [
    { input: '/ɡoʊ ˈʃɒpɪŋ/', description: 'go shopping' },
    { input: '/ˈtaɪɡər/', description: 'tiger' },
    { input: '/ber/', description: 'bear' },
    { input: '/hɑːt/', description: 'hot' },
    { input: '/noʊ/', description: 'no' },
    { input: '/ˈtiːtʃər/', description: 'teacher' },
  ];

  console.log('Phonetic Conversion Test Results:');
  console.log('====================================');

  testCases.forEach(({ input, description }) => {
    const converted = convertToBothPhonetics(input);
    const isDifferent = converted.american !== converted.british;

    console.log(`${description}:`);
    console.log(`  Input:    ${input}`);
    console.log(`  American: ${converted.american}`);
    console.log(`  British:  ${converted.british}`);
    if (isDifferent) {
      console.log(`  ⚠️  Different!`);
    }
    console.log('');
  });
}